import { useState } from 'react';
import styled from '@emotion/styled';
import { Trans, t } from '@lingui/macro';
import { CaretRight, Check } from '@phosphor-icons/react';
import { CircularProgress } from '@mui/material';

import { Search } from 'components/inputs/Search';
import type { SwitchableCompany } from '../hooks/useCompanySwitcher';
import { useTheme } from '@emotion/react';
import { Text } from 'components/text/Text';
import { Avatar } from 'components/avatar/Avatar';

type SwitchCompanyModalProps = {
  companies: SwitchableCompany[];
  isLoading: boolean;
  // May return a promise (legacy switch) so we can keep rows disabled until it settles.
  onSwitch: (company: SwitchableCompany) => Promise<unknown> | void;
  // External in-flight signal (e.g. a mutation already running elsewhere).
  isSwitching?: boolean;
};

// Body of the "Switch company" dialog (the modal chrome — title, subtitle, close — is
// supplied by the modalManager dialog that renders this). Figma: Components (WIP) 10132:2931.
//
// Presentational only: the modalManager renders dialog children OUTSIDE the <Router> tree,
// so router/query/store hooks must NOT live here — the data and switch action are supplied
// by the caller (UserDetails, inside the Router) via props.
export const SwitchCompanyModal = ({
  companies,
  isLoading,
  onSwitch,
  isSwitching = false,
}: SwitchCompanyModalProps) => {
  const [search, setSearch] = useState('');
  // The key of the row the user is switching into; drives the per-row spinner + disabled state.
  // Owned here because the dialog renders as a detached snapshot, so it can't observe the
  // switcher hook's state reactively (see useCompanySwitcherDialog).
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const { palette } = useTheme();

  const isBusy = isSwitching || pendingKey !== null;

  const handleSwitch = (company: SwitchableCompany) => {
    if (isBusy) return;
    setPendingKey(company.key);
    // Reset when the switch settles so a failed switch re-enables the rows for a retry
    // (a successful one closes the dialog first, unmounting this component). onSwitch never
    // rejects — failures are surfaced by the switch hook — so a bare `.finally` is enough.
    void Promise.resolve(onSwitch(company)).finally(() => setPendingKey(null));
  };

  const query = search?.trim()?.toLowerCase();
  const filtered = query
    ? companies?.filter(company => company?.organizationName?.toLowerCase().includes(query))
    : companies;

  return (
    <Wrapper>
      <Search
        value={search}
        onChange={setSearch}
        fullWidth
        searchOpen
        onSearchOpenChange={() => undefined}
        placeholder={t`Search`}
        enableKeyboardShortcuts={false}
        data-testid="switch-company-search"
      />
      <Section>
        <Text variant="bodySm" weight="semiBold">
          <Trans>All companies</Trans>
        </Text>
        {isLoading ? (
          <LoaderRow>
            <CircularProgress size={20} />
          </LoaderRow>
        ) : filtered.length === 0 ? (
          <Text variant="bodySm" color="secondary">
            <Trans>No companies found</Trans>
          </Text>
        ) : (
          <List>
            {filtered.map(company => (
              <Row
                key={company.key}
                type="button"
                onClick={() => handleSwitch(company)}
                disabled={isBusy}
                aria-current={company.isCurrent || undefined}
                aria-busy={pendingKey === company.key || undefined}
                data-testid="switch-company-row"
                isCurrent={company.isCurrent}
              >
                <Identity>
                  <Avatar size="sm" letters={company.organizationName} />
                  <Text variant="bodySm">{company.organizationName}</Text>
                </Identity>
                <Meta>
                  {company.isCurrent && (
                    <Check
                      size={16}
                      weight="bold"
                      data-testid="current-company-check"
                      color={palette.text.brandTertiary}
                    />
                  )}
                  <Text variant="bodySm">
                    {company.type === 'supplier' ? (
                      <Trans>Supplier</Trans>
                    ) : (
                      <Trans>Designer</Trans>
                    )}
                  </Text>
                  {pendingKey === company.key ? (
                    <CircularProgress size={16} data-testid="switch-company-row-spinner" />
                  ) : (
                    <CaretRight size={20} />
                  )}
                </Meta>
              </Row>
            ))}
          </List>
        )}
      </Section>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.lg};
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.md};
  flex: 1;
  min-height: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xs};

  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Row = styled.button<{ isCurrent?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  padding: ${p => p.theme.s.xs};
  margin: 0;
  border-radius: ${p => p.theme.s.xs};

  background: ${p => (p.isCurrent ? p.theme.palette.bg.brandSubtle : 'none')};

  &:not(:disabled):hover {
    background: ${p => p.theme.palette.bg.secondary};
  }

  &:disabled {
    cursor: default;
  }

  /* While a switch is in flight, dim the rows the user isn't switching into. */
  &:disabled:not([aria-busy='true']) {
    opacity: 0.6;
  }
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xs};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xs};
`;

const LoaderRow = styled.div`
  display: flex;
  justify-content: center;
  padding: ${p => p.theme.s.md};
`;
